import React, { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calculator as CalcIcon, ArrowRight } from "lucide-react";
import { createLead, trackEvent } from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";

const PROPERTY_TYPES = ["Apartment", "Villa", "Independent House", "Office", "Other"];
const HOME_SIZES = [
    { key: "1BHK", label: "1 BHK", base: 32000 },
    { key: "2BHK", label: "2 BHK", base: 47999 },
    { key: "3BHK", label: "3 BHK", base: 63999 },
    { key: "4BHK", label: "4 BHK", base: 79999 },
    { key: "5BHK", label: "5 BHK", base: 99999 },
    { key: "CUSTOM", label: "Custom", base: 55000 },
];

const AUTOMATIONS = [
    { key: "lighting", label: "Lighting", add: 4000 },
    { key: "fans", label: "Fans", add: 3000 },
    { key: "ac", label: "AC", add: 6000 },
    { key: "curtains", label: "Curtains", add: 8000 },
    { key: "tv", label: "TV / Entertainment", add: 3500 },
    { key: "security", label: "Security", add: 7000 },
    { key: "locks", label: "Door Locks", add: 9000 },
    { key: "cameras", label: "Cameras", add: 6500 },
    { key: "kitchen", label: "Kitchen Appliances", add: 4000 },
    { key: "waterheater", label: "Water Heater", add: 3000 },
    { key: "energy", label: "Energy Management", add: 5000 },
];

export default function Calculator() {
    usePageTitle("Cost Calculator");
    const [propertyType, setPropertyType] = useState("Apartment");
    const [homeSize, setHomeSize] = useState("2BHK");
    const [selected, setSelected] = useState({ lighting: true, fans: true });
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const estimate = useMemo(() => {
        const sizeBase = HOME_SIZES.find((h) => h.key === homeSize)?.base || 0;
        const addons = AUTOMATIONS.reduce((sum, a) => (selected[a.key] ? sum + a.add : sum), 0);
        return sizeBase + addons;
    }, [homeSize, selected]);

    const handleGetQuote = async (e) => {
        e.preventDefault();
        if (!name || !phone) {
            toast.error("Please enter your name and phone number.");
            return;
        }
        setSubmitting(true);
        try {
            const requirements = AUTOMATIONS.filter((a) => selected[a.key]).map((a) => a.label);
            await createLead({
                name, phone, email,
                property_type: propertyType,
                home_size: homeSize,
                requirements,
                estimated_amount: estimate,
                source_page: "calculator",
            });
            trackEvent("calculator_completion", { estimate, homeSize, requirements });
            toast.success("Thanks! We'll call you with a detailed quote.");
            setName(""); setPhone(""); setEmail("");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="calculator-page">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Smart Home Cost Calculator</div>
                    <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B2E7A] leading-tight" data-testid="calc-title">
                        Estimate your smart home investment.
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-xl">This is an indicative estimate. Your final personalised quote will be shared after a free consultation.</p>

                    <div className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 space-y-8">
                        <div>
                            <label className="text-sm font-medium text-[#1B2E7A]">Property type</label>
                            <div className="mt-2">
                                <Select value={propertyType} onValueChange={setPropertyType}>
                                    <SelectTrigger data-testid="calc-property"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-[#1B2E7A]">Home size</label>
                            <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
                                {HOME_SIZES.map((h) => (
                                    <button
                                        key={h.key}
                                        type="button"
                                        onClick={() => setHomeSize(h.key)}
                                        data-testid={`calc-size-${h.key}`}
                                        className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${
                                            homeSize === h.key
                                                ? "bg-[#1B2E7A] text-white border-[#1B2E7A]"
                                                : "bg-white text-[#1B2E7A] border-slate-200 hover:border-[#1BB6F0]"
                                        }`}
                                    >
                                        {h.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-[#1B2E7A]">What would you like to automate?</label>
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                {AUTOMATIONS.map((a) => (
                                    <label
                                        key={a.key}
                                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                                            selected[a.key]
                                                ? "bg-[#E8F8FE] border-[#1BB6F0]"
                                                : "bg-white border-slate-200 hover:border-[#1BB6F0]/60"
                                        }`}
                                    >
                                        <Checkbox
                                            checked={!!selected[a.key]}
                                            onCheckedChange={(v) => setSelected((s) => ({ ...s, [a.key]: !!v }))}
                                            data-testid={`calc-add-${a.key}`}
                                        />
                                        <span className="text-sm text-[#1B2E7A]">{a.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right sticky summary */}
                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="sticky top-24 rounded-3xl bg-[#0B1440] text-white p-8"
                    >
                        <div className="flex items-center gap-2 text-[#1BB6F0] text-xs tracking-widest uppercase font-semibold">
                            <CalcIcon className="w-4 h-4" /> Estimated Investment
                        </div>
                        <div className="mt-4 font-mono text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="calc-estimate">
                            ₹{estimate.toLocaleString("en-IN")}
                        </div>
                        <div className="mt-1 text-white/60 text-sm">Indicative · Not a final quote</div>

                        <form onSubmit={handleGetQuote} className="mt-6 space-y-3">
                            <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white/[0.06] border-white/15 text-white placeholder:text-white/50" data-testid="calc-name" />
                            <Input placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-white/[0.06] border-white/15 text-white placeholder:text-white/50" data-testid="calc-phone" />
                            <Input placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/[0.06] border-white/15 text-white placeholder:text-white/50" data-testid="calc-email" />
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="calc-submit"
                                className="w-full inline-flex items-center justify-center gap-2 bg-[#1BB6F0] hover:bg-[#1E90FF] disabled:opacity-60 text-white rounded-full px-5 py-3.5 font-medium transition-colors"
                            >
                                {submitting ? "Sending…" : (<>Get Detailed Quote <ArrowRight className="w-4 h-4" /></>)}
                            </button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/60 space-y-1.5">
                            <div>✓ 1-Year warranty included</div>
                            <div>✓ 1-Hour free installation</div>
                            <div>✓ Works with your existing appliances</div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
